import React from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Form, Modal } from 'antd';
import { useTranslation } from 'react-i18next';

import { useNotification } from 'segments/hooks/useNotification';

import { InputStr, SubmiteButton } from 'shared/FormItems';
import { ModalFooter } from 'shared/Modals';

import { UpdateCourtParams, useUpdateCourtMutation, SelectCourtSurface, SelectCourtType, CourtMetainfo } from 'features/courts';
import { SelectImage } from 'features/imagesStore';
import { SelectTimePrice } from 'features/timesPrice';

interface FormData extends Omit<UpdateCourtParams, 'metainfo'> {
  metainfo: CourtMetainfo;
}

interface Props {
  isModalOpen: boolean;
  handleCancel: () => void;
  data?: FormData;
}

const EditCourtModal = ({ isModalOpen, handleCancel, data }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<FormData>();

  const [update, { isSuccess, isError, isLoading, error }] = useUpdateCourtMutation();

  useNotification({
    isSuccess,
    isError,
    onSuccess: () => handleCancel(),
    onError: () => handleCancel(),
    error
  });

  const onFinish = (values: FormData) => {
    const vs = Object.fromEntries(Object.entries(values).filter(([_, v]) => v != null));
    update({ ...vs, id: data?.id } as FormData);
  };

  return (
    <Modal
      title={t('courts.title.edit')}
      open={isModalOpen}
      onCancel={handleCancel}
      destroyOnClose
      footer={[
        <ModalFooter
          key="modalFooter"
          handleCancel={handleCancel}
          resetFields={form.resetFields}
        />
      ]}
    >
      {
        data === undefined
          ? <LoadingOutlined />
          : (
            <Form
              name="basic"
              layout="vertical"
              form={form}
              initialValues={{ ...data }}
              onFinish={onFinish}
              autoComplete="off"
              className="edit-user__form"
            >
              <InputStr type='name' formProps={{ rules: [{ required: true }] }} />
              <SelectCourtSurface />
              <SelectCourtType />
              <SelectImage />
              <SelectTimePrice<FormData> formProps={{ form, initialValue: data.metainfo.pricing }} />
              <SubmiteButton submiteText={t('user.edit-data')} isLoading={isLoading} />
            </Form>
          )
      }
    </Modal >
  );
};

export default EditCourtModal;
